import React, { useState, useEffect, useRef } from "react";
import { Outlet, Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import {
  collection,
  onSnapshot,
  doc,
  getDoc,
  query,
  where,
  getDocs,
  setDoc,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { useT, LANGUAGES } from "../i18n.jsx";
import { signOut } from "firebase/auth";
import { useAuthUser } from "../authState";
import {
  School,
  LogOut,
  Menu,
  Home,
  Palette,
  GraduationCap,
  Star,
  MessageSquare,
  Repeat,
  CalendarCheck,
  Calendar,
  Database,
  Users,
  Table,
  Settings,
  Ticket,
  PenTool,
  UserCog,
  Mail,
  HardDrive,
  Video,
  FileText,
  CheckSquare,
  MessageCircle,
  Laptop,
  Briefcase,
  MapPin,
  Layers,
  Box,
  ChevronDown,
  ChevronRight,
  Lock,
  BookOpen,
  Shield,
  Wallet,
  ClipboardList,
  Accessibility,
  Armchair,
  Loader2,
} from "lucide-react";
import { updatePassword } from "firebase/auth";
import { applySchoolBranding } from '../pwa';
import { tenantFromLocation } from '../tenant';
import { lookupTenant } from '../tenantLookup';
import { FamilyTabBar, FamilyTopBar } from './FamilyNav';
import { InstallBanner, InstallMenuItem } from './InstallPrompt';
import PushToggle from './PushToggle';
import {
  ROLE,
  normaliseRole,
  isAdminRole,
  ACCESS_KEY_FOR,
} from "../roles";

// Who a module is FOR. There is no "both": students and guardians get their OWN module even when it
// performs the same function as a staff one, so a staff module is never simply switched on for a
// family. Access Control groups by this, which is what keeps a school from being asked whether a
// guardian should see Substitution.
//
// Unset means 'staff' -- the overwhelming default, and the safe one. Adding a family module is a
// deliberate act that someone has to ask for.
export const AUDIENCE = { STAFF: 'staff', FAMILY: 'family' };

// What a school sees before anyone opens Access Control. Declared per module rather than derived
// from `category`, which is what it used to be: moving a module between sidebar sections was also a
// permission change, and nobody was ever asked to approve that half of it. Grouping is now a
// presentation decision and this is a permission decision, and they can be changed independently.
//
// Agreed with the platform owner: thirteen modules are everyone's, fifteen belong to a post rather
// than to everyone who teaches, four are the administrator's. A plain teacher therefore opens the
// portal to thirteen entries, not thirty.
const ALL_STAFF = { teacherAccess: "on", staffAccess: "on", studentAccess: "off", parentAccess: "off" };
// Off until the school names who holds the job -- timetable master, discipline, OLE coordinator.
// Granted by name in Access Control, not by being a teacher.
const BY_DUTY = { teacherAccess: "off", staffAccess: "off", studentAccess: "off", parentAccess: "off" };
// Identical values to BY_DUTY, kept separate because the reason is different: system admins bypass
// module_access entirely, so these read "off for everyone the check applies to" rather than
// "waiting to be granted". A future change to one should not silently move the other.
const ADMIN_ONLY = { teacherAccess: "off", staffAccess: "off", studentAccess: "off", parentAccess: "off" };

export const MODULES = [
  {
    id: "my_notice",
    audience: AUDIENCE.FAMILY,
    category: "family",
    name: "My Notice",
    icon: ClipboardList,
    url: "/dashboard/my-notice",
    // Exists for families; staff have the full Notice & Payment module.
    defaultAccess: { teacherAccess: "off", staffAccess: "off", studentAccess: "on", parentAccess: "on" },
  },
  {
    id: "my_attendance",
    audience: AUDIENCE.FAMILY,
    category: "family",
    name: "My Attendance",
    icon: CalendarCheck,
    url: "/dashboard/my-attendance",
    // On for both: a student checks in, a guardian views. Scanning is gated in the module itself,
    // and by the rule -- a create must name the caller's own address, which a guardian's never will.
    defaultAccess: { teacherAccess: "off", staffAccess: "off", studentAccess: "on", parentAccess: "on" },
  },
  {
    id: "my_talent_bank",
    audience: AUDIENCE.FAMILY,
    category: "family",
    name: "My Talent Bank",
    icon: GraduationCap,
    url: "/dashboard/my-talent-bank",
    // User-category modules default to staff-on / student-off, which is backwards for this one:
    // it exists FOR students, and staff have the full Talent Bank. A school can still override it
    // in Access Control; this only changes what happens before anyone configures anything.
    defaultAccess: { teacherAccess: "off", staffAccess: "off", studentAccess: "on", parentAccess: "on" },
  },
  {
    id: "my_messages",
    audience: AUDIENCE.FAMILY,
    category: "family",
    name: "My Messages",
    icon: MessageSquare,
    url: "/dashboard/my-messages",
    // Parent OFF: threads are staff-to-staff or staff-to-student and a guardian is never a
    // participant, so this module would always be empty for them. A school can still switch it on
    // if that ever changes.
    defaultAccess: { teacherAccess: "off", staffAccess: "off", studentAccess: "on", parentAccess: "off" },
  },
  {
    id: "dashboard",
    defaultAccess: ALL_STAFF,
    category: "me",
    name: "Dashboard",
    icon: Home,
    url: "/dashboard",
  },
  {
    // The assignee's side of the four duty modules. Those are assignment tools -- Allocation,
    // Schedule, Statistics, Settings -- and none of them has a "mine" view, so before this a school
    // had to switch the whole machinery on for every teacher just so they could see what they were
    // rostered for. Everyone gets this one; the assignment modules stay with the coordinators.
    id: "my_duties",
    defaultAccess: ALL_STAFF,
    category: "me",
    name: "My Duties",
    icon: ClipboardList,
    url: "/dashboard/my-duties",
  },
  {
    id: "my_timetable",
    defaultAccess: ALL_STAFF,
    category: "me",
    name: "My Timetable",
    icon: Calendar,
    url: "/dashboard/my-timetable",
  },

  // ASSET MANAGEMENT,
  {
    id: "my_message",
    defaultAccess: ALL_STAFF,
    category: "me",
    name: "My Message",
    icon: MessageSquare,
    url: "/dashboard/my-message",
  },
  {
    id: "forms_and_application",
    defaultAccess: ALL_STAFF,
    category: "me",
    name: "Forms and Application",
    icon: FileText,
    url: "/dashboard/forms-and-application",
  },
  // Students, in the order the office actually reaches for them -- daily first, occasional last.
  // The sidebar renders this section in array order, so this order IS the order on screen.
  {
    id: "attendance",
    defaultAccess: ALL_STAFF,
    category: "students",
    name: "Attendance",
    icon: CalendarCheck,
    url: "/dashboard/attendance",
  },
  {
    id: "social_credit",
    defaultAccess: BY_DUTY,
    category: "students",
    name: "Student Credit",
    icon: Star,
    url: "/dashboard/social-credit",
  },
  {
    id: "ole_management",
    defaultAccess: BY_DUTY,
    category: "students",
    name: "OLE Management",
    icon: Layers,
    url: "/dashboard/ole-management",
  },
  {
    id: "talent_bank",
    defaultAccess: ALL_STAFF,
    category: "students",
    name: "Talent Bank",
    icon: GraduationCap,
    url: "/dashboard/talent-bank",
  },
  {
    id: "parent_notice",
    defaultAccess: BY_DUTY,
    category: "students",
    name: "Notice & Payment",
    icon: ClipboardList,
    url: "/dashboard/parent-notice",
  },
  {
    // Where a class sits, room by room. Groups exist because plenty of lessons are not the whole
    // class -- an elective, a split set -- and those need a seating plan just as much.
    id: "seating_plan",
    defaultAccess: ALL_STAFF,
    category: "students",
    name: "Seating Plan",
    icon: Armchair,
    url: "/dashboard/seating-plan",
  },
  {
    // Revision scopes. Anchored to the assessment list rather than a free-text exam name, so a
    // scope and the marks that follow it are talking about the same sitting.
    //
    // Everyone on staff, unlike the other Students-section modules: any teacher writes the scope
    // for their own subject. What is restricted is publishing it, and that is not a module switch
    // -- it is the panel head, worked out from Teaching Duty.
    id: "exam_scope",
    defaultAccess: ALL_STAFF,
    category: "assessments",
    name: "Exam Scope",
    icon: BookOpen,
    url: "/dashboard/exam-scope",
  },
  {
    id: "assessment",
    defaultAccess: BY_DUTY,
    category: "assessments",
    name: "Academic Record",
    icon: CheckSquare,
    url: "/dashboard/assessment",
  },
  {
    id: "quiz_tutorial",
    defaultAccess: BY_DUTY,
    category: "assessments",
    name: "Quiz and Tutorial",
    icon: CalendarCheck,
    url: "/dashboard/quiz-tutorial",
  },
  {
    id: "calendar_admin",
    defaultAccess: ALL_STAFF,
    category: "administration",
    name: "School Calendar",
    icon: Calendar,
    url: "/dashboard/calendar-admin",
  },
  {
    id: "teacher_timetable",
    defaultAccess: ALL_STAFF,
    category: "administration",
    name: "Teacher Timetable",
    icon: Table,
    url: "/dashboard/teacher-timetable",
  },
  {
    id: "timetable_profiles",
    defaultAccess: BY_DUTY,
    category: "administration",
    name: "Timetable Profiles",
    icon: Settings,
    url: "/dashboard/timetable-profiles",
  },
  {
    // Coordinator's tool. It was everyone's until My Duties existed, because a teacher had no
    // other way to find out they were covering P3 -- but its five tabs are Statistics, Absence,
    // Substitution, Arrange and Settings, and since the duty rules tightened a teacher cannot write
    // any of it. What they actually needed is their own rows and their own balance, and that now
    // lives in My Duties.
    id: "substitution",
    defaultAccess: BY_DUTY,
    category: "duties",
    name: "Substitution",
    icon: Repeat,
    url: "/dashboard/substitution",
  },
  {
    id: "teaching_duty",
    defaultAccess: BY_DUTY,
    category: "duties",
    name: "Subject Panel",
    icon: BookOpen,
    url: "/dashboard/teaching-duty",
  },

  // SYSTEM,
  {
    id: "manpower_allocation",
    defaultAccess: BY_DUTY,
    category: "duties",
    name: "Admin Duty",
    icon: UserCog,
    url: "/dashboard/manpower-allocation",
  },
  {
    id: "invigilation",
    defaultAccess: BY_DUTY,
    category: "duties",
    name: "Invigilation",
    icon: Users,
    url: "/dashboard/invigilation",
  },
  {
    id: "sen_invigilation",
    defaultAccess: BY_DUTY,
    category: "duties",
    name: "SEN Invigilation",
    icon: Accessibility,
    url: "/dashboard/sen-invigilation",
  },
  {
    id: "patrol_duty",
    defaultAccess: BY_DUTY,
    category: "duties",
    name: "Patrol Duty",
    icon: MapPin,
    url: "/dashboard/patrol-duty",
  },
  {
    id: "facility_booking",
    defaultAccess: ALL_STAFF,
    category: "booking",
    name: "Facility Booking",
    icon: MapPin,
    url: "/dashboard/facility-booking",
  },
  {
    id: "borrow_devices",
    defaultAccess: ALL_STAFF,
    category: "booking",
    name: "Borrow IT Devices",
    icon: Laptop,
    url: "/dashboard/mdm-user",
  },
  {
    id: "budget_control",
    defaultAccess: BY_DUTY,
    category: "accounting",
    name: "Budget Control",
    icon: Briefcase,
    url: "/dashboard/budget-control",
  },
  {
    id: "inventory_admin",
    defaultAccess: BY_DUTY,
    category: "accounting",
    name: "Inventory",
    icon: Box,
    url: "/dashboard/inventory-admin",
  },
  {
    id: "salary_audit",
    defaultAccess: BY_DUTY,
    category: "accounting",
    name: "Salary Audit",
    icon: Wallet,
    url: "/dashboard/salary-audit",
  },

  // STUDENT AFFAIRS,
  {
    id: "campus_management",
    defaultAccess: BY_DUTY,
    category: "resources",
    name: "Campus",
    icon: Layers,
    url: "/dashboard/campus-management",
  },
  {
    id: "manage_it_assets",
    defaultAccess: BY_DUTY,
    category: "resources",
    name: "IT Assets",
    icon: Laptop,
    url: "/dashboard/mdm-admin",
  },
  {
    id: "inventory_user",
    defaultAccess: ALL_STAFF,
    category: "resources",
    name: "My Inventory",
    icon: Box,
    url: "/dashboard/inventory-user",
  },
  {
    id: "maintenance_report",
    defaultAccess: ALL_STAFF,
    category: "resources",
    name: "Maintenance Report",
    icon: PenTool,
    url: "/dashboard/maintenance-report",
  },

  // SCHOOL ADMINISTRATION,
  {
    id: "user_mgmt",
    defaultAccess: ADMIN_ONLY,
    category: "system",
    name: "Staff Account",
    icon: Users,
    url: "/dashboard/staff-mgt",
  },
  {
    id: "data_mgmt",
    defaultAccess: ADMIN_ONLY,
    category: "system",
    name: "Student Account",
    icon: Database,
    url: "/dashboard/student-mgt",
  },
  {
    id: "system_groups",
    defaultAccess: ADMIN_ONLY,
    category: "system",
    name: "Access Control",
    icon: Shield,
    url: "/dashboard/access-control",
  },
  {
    id: "system_settings",
    defaultAccess: ADMIN_ONLY,
    category: "system",
    name: "System Settings",
    icon: Settings,
    url: "/dashboard/system-settings",
  },
];

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Portal target for a module's contextual tab-bar controls (filters, Add/Save/CSV buttons) on
  // mobile, so they render next to the retractable sidebar icon in the header instead of wrapping
  // onto their own row inside the module. A callback ref (not useRef) so we re-render once the node
  // actually exists -- modules read this via useOutletContext and only portal into it once it's set,
  // rendering inline as normal on desktop where the slot is never mounted.
  const [headerActionsEl, setHeaderActionsEl] = useState(null);

  // Whether the viewport is currently mobile-width -- tracked via a resize listener (not just read
  // once during render) so the sidebar/hamburger correctly react to rotating a tablet or resizing a
  // desktop window across the breakpoint, not just whatever size the page happened to be on mount.
  const { t, language, setLanguage, setSchoolId: setLangSchoolId } = useT();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile((prevMobile) => {
        // Crossing the breakpoint closes the (mobile-only) slide-out sidebar so it doesn't get stuck
        // open as a fixed overlay once the layout switches to the always-visible desktop sidebar.
        if (prevMobile !== mobile) {
          setSidebarOpen(false);
          setSidebarEntered(false);
          setSidebarDragging(false);
          setSidebarDragX(0);
        }
        return mobile;
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Sidebar open/close animation + swipe-to-dismiss (mobile only), same pattern as
  // HeaderActionsPortal's slide-out drawer: mount at translateX(-100%), flip to 0 on the next frame
  // to animate in; on close, animate back to -100% then actually unmount/hide after the transition.
  // Dragging tracks the finger 1:1 (transition off) and only allows movement toward closed (negative
  // X, since the sidebar lives on the left), snapping back open or animating closed on release.
  const [sidebarEntered, setSidebarEntered] = useState(false);
  const [sidebarDragging, setSidebarDragging] = useState(false);
  const [sidebarDragX, setSidebarDragX] = useState(0);
  const sidebarStartXRef = useRef(0);
  const SIDEBAR_TRANSITION_MS = 220;
  const SIDEBAR_DRAG_CLOSE_THRESHOLD = 80;

  const openSidebar = () => {
    setSidebarOpen(true);
    requestAnimationFrame(() => requestAnimationFrame(() => setSidebarEntered(true)));
  };

  const closeSidebar = () => {
    setSidebarEntered(false);
    setSidebarDragging(false);
    setSidebarDragX(0);
    setTimeout(() => setSidebarOpen(false), SIDEBAR_TRANSITION_MS);
  };

  const handleSidebarDragStart = (e) => {
    if (!isMobile) return;
    sidebarStartXRef.current = e.touches ? e.touches[0].clientX : e.clientX;
    setSidebarDragging(true);
  };
  const handleSidebarDragMove = (e) => {
    if (!sidebarDragging) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    setSidebarDragX(Math.min(0, x - sidebarStartXRef.current));
  };
  const handleSidebarDragEnd = () => {
    if (!sidebarDragging) return;
    if (sidebarDragX < -SIDEBAR_DRAG_CLOSE_THRESHOLD) {
      closeSidebar();
    } else {
      setSidebarDragging(false);
      setSidebarDragX(0);
    }
  };

  const sidebarTransform = sidebarEntered ? `translateX(${sidebarDragX}px)` : 'translateX(-100%)';

  // Collapse state (true means collapsed)
  // Open by default, because a section that renders at all is one this account has modules in --
  // the guard below drops empty headings entirely, so a plain teacher sees four headings over
  // thirteen entries and nothing to expand. System is the exception: it is the administrator's, and
  // it is never what anyone is in the middle of doing.
  //
  // Remembered per device. Collapsing a section you never use is only worth doing if it stays
  // collapsed; before this it reset on every reload, which is why nobody did it.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("school_portal_sidebar_collapsed"));
      if (saved && typeof saved === "object") return saved;
    } catch { /* a corrupt value is not worth a blank screen */ }
    return { me: false, students: false, assessments: false, administration: false, duties: false, accounting: false, resources: false, booking: false, system: true };
  });

  // Retracted to an icon rail. Remembered per device, like the section collapse above and for the
  // same reason: a width you have to set again on every load is one nobody sets.
  //
  // Default expanded. The labels are how somebody learns what is in here; the rail is what you
  // choose once you already know.
  const [railed, setRailed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("school_portal_sidebar_railed")) === true; }
    catch { return false; }
  });

  // Never on a phone: there the sidebar is a full-screen drawer you open, pick from, and dismiss,
  // so a rail would be a second drawer with less in it.
  const rail = railed && !isMobile;

  const toggleRail = () => {
    setRailed((prev) => {
      const next = !prev;
      try { localStorage.setItem("school_portal_sidebar_railed", JSON.stringify(next)); }
      catch { /* private mode */ }
      return next;
    });
  };

  const [requirePasswordChange, setRequirePasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordChangeError, setPasswordChangeError] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Favorites state
  const [favorites, setFavorites] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("school_portal_favorites")) || [];
    } catch {
      return [];
    }
  });

  const [schools, setSchools] = useState([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState(() => {
    return localStorage.getItem("school_portal_active_school") || null;
  });

  const [activeModules, setActiveModules] = useState({});
  const [tenantModules, setTenantModules] = useState({});
  const [moduleAccessConfig, setModuleAccessConfig] = useState({});
  // Whether the three documents isModuleActive() reads have answered yet.
  //
  // They all start as {}, and an empty config grants nothing -- so for the first moments of every
  // hard refresh the guard below concluded the module was switched off and said so. "Module is Off"
  // is a statement about how the school configured the platform; saying it because a read has not
  // come back yet is telling the user something untrue about their own school.
  const [accessReady, setAccessReady] = useState(false);
  const [schoolMetadata, setSchoolMetadata] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  // userProfile === null means two different things -- still loading, and resolved to nothing -- and
  // conflating them is why a broken account would show an empty app rather than an explanation.
  const [profileResolved, setProfileResolved] = useState(false);

  // Multi-school guardians. A parent may have children at two schools on the platform, which the
  // single-value schoolId claim cannot express -- guardian_schools carries the list instead.
  //
  // This only ever runs in the branch that used to be a dead end: no subdomain, no remembered
  // school. Every existing path -- staff, students, and any guardian arriving at a school's own
  // subdomain -- resolves exactly as it did before, which is deliberate: this effect is on the
  // critical path of every page in the platform.
  const [schoolOverride, setSchoolOverride] = useState(null);

  const getTenantFromUrl = () => tenantFromLocation();

  const navigate = useNavigate();
  const location = useLocation();
  // Reactive, and it has to be. auth.currentUser is null on the first tick of every cold start while
  // Firebase restores the session from storage, and reading it once means that null is what the whole
  // component sees for the rest of its life -- every effect keyed on user?.email bails, and nothing
  // ever re-renders to correct it. Someone opening /dashboard directly got a shell with no data.
  const user = useAuthUser();
  const authResolved = user !== undefined;

  useEffect(() => {
    if (!user?.email) return;

    let unsubSchool = null;

    const fetchUserAndSchool = async () => {
      try {
        let targetSchoolId = schoolOverride
          || localStorage.getItem("school_portal_active_school");
        const urlTenant = getTenantFromUrl();

        if (urlTenant) {
          // The address carries a label, not an id. The public lookup is what turns one into the
          // other -- see src/tenantLookup.js for why this is not a query over schools.
          const school = await lookupTenant(urlTenant);
          if (school) {
            targetSchoolId = school.schoolId;
            localStorage.setItem("school_portal_active_school", targetSchoolId);
          } else {
            // Invalid tenant domain
            targetSchoolId = null;
          }
        }

        // Nothing resolved yet. Before giving up, check whether this is a guardian -- the list is
        // readable only by the guardian themselves, so a failure here just means "not one".
        if (!targetSchoolId) {
          try {
            const gSnap = await getDoc(doc(db, "guardian_schools", user.email.toLowerCase()));
            const ids = (gSnap.exists() ? gSnap.data().schoolIds : null) || [];

            // More than one is not asked about. A guardian with children at two schools opens
            // the other school's own address -- the switcher was a second way to do what the
            // subdomain already does, and it earned its keep for almost nobody.
            if (ids.length >= 1) {
              targetSchoolId = ids[0];
              localStorage.setItem("school_portal_active_school", targetSchoolId);
            }
          } catch (e) {
            // Not a guardian, or the document is unreadable. Either way this is not an error worth
            // surfacing -- the screen below already explains having no school.
          }
        }

        if (targetSchoolId) {
          setSelectedSchoolId(targetSchoolId);
          unsubSchool = onSnapshot(
            doc(db, "schools", targetSchoolId),
            (snap) => {
              if (snap.exists()) {
                const sData = { id: snap.id, ...snap.data() };
                setSchools([sData]);
              } else {
                // School document was deleted or invalid
                localStorage.removeItem("school_portal_active_school");
                if (!getTenantFromUrl()) {
                  navigate("/");
                }
              }
            },
            (err) => {
              console.error("Error fetching school doc:", err);
            },
          );

          // Resolve accountId
          let accountId = user.email.toLowerCase();
          try {
            const indexDoc = await getDoc(
              doc(db, "schools", targetSchoolId, "email_index", accountId),
            );
            if (indexDoc.exists() && indexDoc.data().accountId) {
              accountId = indexDoc.data().accountId;
            }
          } catch (e) {
            console.warn(
              "Could not lookup email_index, falling back to email.",
            );
          }

          // Fetch profile data with error handling

          let profileData = null;
          const mapStaffData = (data) => {
            return {
              ...data,
              // Canonicalised here, once. staff_profiles.role holds whatever the CSV import wrote,
              // so every consumer downstream would otherwise be re-guessing at free text.
              role: normaliseRole(data.role || ROLE.NON_TEACHING),
              name:
                data.name ||
                data.displayName ||
                `${data.lastName || ""} ${data.firstName || ""}`.trim() ||
                "Staff",
            };
          };
          const mapStudentData = (data) => {
            return {
              ...data,
              role: normaliseRole(data.role || ROLE.STUDENT),
              name:
                `${data.lastName || ""} ${data.firstName || ""}`.trim() ||
                data.name ||
                data.displayName ||
                "Student",
              studentId: data.sid || "",
            };
          };

          let staffData = null;
          try {
            const staffDoc = await getDoc(
              doc(db, "schools", targetSchoolId, "staff_profiles", accountId),
            );
            if (staffDoc.exists()) {
              staffData = staffDoc.data();
            }
          } catch (e) {
            // Silently ignore permissions errors
          }

          let studentData = null;
          try {
            const studentDoc = await getDoc(
              doc(db, "schools", targetSchoolId, "student_profiles", accountId),
            );
            if (studentDoc.exists()) {
              studentData = studentDoc.data();
            }
          } catch (e) {
            // Silently ignore permissions errors
          }

          if (staffData) {
            profileData = mapStaffData(staffData);
          } else if (studentData) {
            profileData = mapStudentData(studentData);
          } else {
            const emailStr = user.email.toLowerCase();

            try {
              const qStaff = query(
                collection(db, "schools", targetSchoolId, "staff_profiles"),
                where("email", "==", emailStr),
              );
              const snapStaff = await getDocs(qStaff);
              if (!snapStaff.empty) {
                profileData = mapStaffData(snapStaff.docs[0].data());
              }
            } catch (e) {}

            if (!profileData) {
              try {
                const qStudent = query(
                  collection(db, "schools", targetSchoolId, "student_profiles"),
                  where("email", "==", emailStr),
                );
                const snapStudent = await getDocs(qStudent);
                if (!snapStudent.empty) {
                  profileData = mapStudentData(snapStudent.docs[0].data());
                }
              } catch (e) {}
            }
          }
          // A guardian has neither a staff nor a student profile -- they are identified by being
          // named as guardianEmail on one or more student records. Their children come back from the
          // same query, which is what Parent Notice uses to scope what they can see and sign.
          if (!profileData) {
            try {
              // Two separate equality queries rather than one `or`: each has to be provable
              // against its own disjunct in the security rule, and Firestore cannot prove an OR of
              // two different fields from a single query.
              const guardianAddr = user.email.toLowerCase();
              // allSettled, not all: these are two independent lookups, and if one is refused --
              // an older ruleset with no guardianEmail2 branch, say -- Promise.all would discard the
              // other one's result too and the guardian would resolve to nothing at all. Failing to
              // find a second guardian must never cost someone their first.
              const settled = await Promise.allSettled([
                getDocs(query(
                  collection(db, "schools", targetSchoolId, "student_profiles"),
                  where("guardianEmail", "==", guardianAddr),
                )),
                getDocs(query(
                  collection(db, "schools", targetSchoolId, "student_profiles"),
                  where("guardianEmail2", "==", guardianAddr),
                )),
              ]);
              settled.forEach(r => {
                if (r.status === "rejected") {
                  console.warn("Guardian lookup partly failed", r.reason);
                }
              });
              const [snapFirst, snapSecond] = settled.map(r => (
                r.status === "fulfilled" ? r.value : { empty: true, docs: [] }
              ));
              const seenChildren = new Set();
              const snapGuardian = {
                empty: snapFirst.empty && snapSecond.empty,
                docs: [...snapFirst.docs, ...snapSecond.docs].filter(d => {
                  if (seenChildren.has(d.id)) return false;
                  seenChildren.add(d.id);
                  return true;
                }),
              };
              if (!snapGuardian.empty) {
                const children = snapGuardian.docs.map((d) => {
                  const c = d.data();
                  return {
                    sid: c.sid || "",
                    email: (c.email || d.id || "").toLowerCase(),
                    name: `${c.lastName || ""} ${c.firstName || ""}`.trim() || c.sid || "",
                    currentClass: c.currentClass || "",
                    classNumber: c.classNumber || "",
                  };
                });
                profileData = {
                  role: "parent",
                  name: t("family.guardianOf", { names: children.map((c) => c.name).join(", ") }),
                  children,
                };
              }
            } catch (e) {
              // Silently ignore permission errors
            }
          }

          if (profileData && profileData.requirePasswordChange) {
            setRequirePasswordChange(true);
          }

          // Developer accounts get full access when previewing/testing a tenant,
          // even if they have no staff/student profile document in that school.
          try {
            const devDoc = await getDoc(
              doc(db, "developers", user.email.toLowerCase()),
            );
            if (devDoc.exists()) {
              profileData = {
                ...(profileData || {}),
                role: ROLE.DEVELOPER,
                name: profileData?.name || "Developer",
              };
            }
          } catch (e) {
            // Silently ignore permission errors
          }

          setUserProfile(profileData);
        }
      } catch (err) {
        // Silently ignore permission errors when checking school assignment
      } finally {
        // Resolved, whatever the outcome. A failure here means "we looked and found nothing",
        // which is a different state from "we have not looked yet".
        setProfileResolved(true);
      }
    };

    fetchUserAndSchool();

    return () => {
      if (unsubSchool) unsubSchool();
    };
  }, [user?.email, schoolOverride]);

  // The language preference lives under the school, so i18n cannot read it until the tenant is
  // known -- which is here, and only here.
  useEffect(() => { setLangSchoolId(selectedSchoolId || null); }, [selectedSchoolId, setLangSchoolId]);

  // Fetch activeModules, entitledModules, branding, and dashboardConfig when selectedSchoolId changes
  useEffect(() => {
    if (!selectedSchoolId) return;
    // Reset first: switching school makes the previous school's answers meaningless, and leaving
    // the flag up would flash the old tenant's verdict on the new one's modules.
    setAccessReady(false);
    const seen = { settings: false, flags: false, lists: false };
    // Settled, not succeeded. A read that FAILED has also told us everything it is going to tell
    // us; leaving the flag down for it would spin forever instead of falling through to the
    // module's declared default, which is what the code below already does.
    const settle = (which) => {
      seen[which] = true;
      if (seen.settings && seen.flags && seen.lists) setAccessReady(true);
    };
    const unsubSettings = onSnapshot(
      doc(db, "schools", selectedSchoolId, "metadata", "system_settings"),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          if (data.moduleStatus) setActiveModules(data.moduleStatus);
          if (data.entitledModules) setTenantModules(data.entitledModules);
          setSchoolMetadata(data);
          // Tell the browser which school this is, so an installed app carries the school's name
          // and badge instead of the platform's. Done here because this is where the tenant is
          // first known -- it is the same document the sidebar already brands itself from.
          applySchoolBranding({
            name: data.schoolName || '',
            logoUrl: data.branding?.logoUrl || '',
            themeColor: data.branding?.primaryColor || '',
          });
        }
        settle("settings");
      },
      (err) => {
        console.error("Error fetching system_settings in Layout:", err);
        settle("settings");
      },
    );
    // Which modules each role sees, and -- for staff only -- who has a per-person exception.
    //
    // These are two documents because the exception lists hold email addresses. module_access used
    // to hold both and was staff-readable, so students and guardians could not read it at all: their
    // config stayed empty, Layout fell back to each module's built-in default, and every switch on
    // the family tab of Access Control did nothing to the people it configures. The only trace was
    // one console line about insufficient permissions.
    //
    // Merged back together here so isModuleActive below is unchanged.
    let accessFlags = {};
    let accessLists = {};
    const mergeAccess = () => {
      const merged = {};
      new Set([...Object.keys(accessFlags), ...Object.keys(accessLists)]).forEach((id) => {
        merged[id] = { ...(accessFlags[id] || {}), ...(accessLists[id] || {}) };
      });
      setModuleAccessConfig(merged);
    };

    const unsubAccess = onSnapshot(
      doc(db, "schools", selectedSchoolId, "metadata", "module_access"),
      (snap) => {
        accessFlags = snap.exists() ? snap.data() : {};
        mergeAccess();
        settle("flags");
      },
      (err) => {
        console.error("Error fetching module_access in Layout:", err);
        settle("flags");
      },
    );

    const unsubAccessLists = onSnapshot(
      doc(db, "schools", selectedSchoolId, "metadata", "module_access_exceptions"),
      (snap) => {
        accessLists = snap.exists() ? snap.data() : {};
        mergeAccess();
        settle("lists");
      },
      // Staff-only by design. A student or guardian being refused here is the expected outcome, not
      // a fault -- exceptions are a staff-side feature and the family columns no longer offer them.
      () => { accessLists = {}; mergeAccess(); settle("lists"); },
    );

    return () => {
      unsubSettings();
      unsubAccess();
      unsubAccessLists();
    };
  }, [selectedSchoolId]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/");
  };

  const toggleCategory = (cat) => {
    setCollapsed((prev) => {
      const next = { ...prev, [cat]: !prev[cat] };
      // Written here rather than in an effect: this is the only thing that changes it, and an
      // effect would also fire on the initial read and write back what it just read.
      try { localStorage.setItem("school_portal_sidebar_collapsed", JSON.stringify(next)); } catch { /* private mode */ }
      return next;
    });
  };

  const toggleFavorite = (e, moduleId) => {
    e.preventDefault();
    e.stopPropagation();
    if (favorites.includes(moduleId)) {
      const newFavs = favorites.filter((id) => id !== moduleId);
      setFavorites(newFavs);
      localStorage.setItem("school_portal_favorites", JSON.stringify(newFavs));
    } else {
      if (favorites.length >= 5) {
        alert("You can only choose at most 5 commonly used modules.");
        return;
      }
      const newFavs = [...favorites, moduleId];
      setFavorites(newFavs);
      localStorage.setItem("school_portal_favorites", JSON.stringify(newFavs));
    }
  };

  const renderModuleLink = (module) => {
    const Icon = module.icon;
    const isActive = location.pathname === module.url;
    const isFav = favorites.includes(module.id);
    const label = t(`nav.modules.${module.id}`, {}, module.name);

    // On the rail the icon is the whole row. The label is gone, so `title` carries the tooltip and
    // aria-label carries the name a screen reader reads -- an icon with neither is a button that
    // only its author can use. The favourite star goes too: there is nowhere to put it at 72px,
    // and starring is not what anyone opened a collapsed menu to do.
    if (rail) {
      const railStyle = {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0.7rem 0",
        textDecoration: "none",
        color: isActive ? "var(--color-primary)" : "var(--color-text-muted)",
        backgroundColor: isActive ? "var(--color-nav-active)" : "transparent",
        boxShadow: isActive ? "inset 3px 0 0 var(--color-accent)" : "none",
      };
      return module.external ? (
        <a
          key={module.id}
          href={module.url}
          target="_blank"
          rel="noreferrer"
          title={label}
          aria-label={label}
          style={railStyle}
        >
          <Icon size={20} />
        </a>
      ) : (
        <Link
          key={module.id}
          to={module.url}
          onClick={closeSidebar}
          title={label}
          aria-label={label}
          style={railStyle}
        >
          <Icon size={20} />
        </Link>
      );
    }

    if (module.external) {
      return (
        <div key={module.id} style={{ position: "relative" }}>
          <a
            href={module.url}
            target="_blank"
            rel="noreferrer"
            style={{
              display: "flex",
              alignItems: "center",
              padding: "0.75rem 1.5rem",
              color: "var(--color-text-main)",
              textDecoration: "none",
              transition: "background-color 0.2s",
              paddingRight: "3rem",
            }}
          >
            <Icon
              size={18}
              style={{ marginRight: "1rem", color: "var(--color-primary)" }}
            />
            <span style={{ flex: 1 }}>{t(`nav.modules.${module.id}`, {}, module.name)}</span>
          </a>
          <button
            onClick={(e) => toggleFavorite(e, module.id)}
            style={{
              position: "absolute",
              right: "1rem",
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "0.2rem",
            }}
          >
            <Star
              size={16}
              fill={isFav ? "var(--color-warning)" : "none"}
              color={isFav ? "var(--color-warning)" : "var(--color-text-muted)"}
            />
          </button>
        </div>
      );
    }

    return (
      <div key={module.id} style={{ position: "relative" }}>
        <Link
          to={module.url}
          onClick={closeSidebar}
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0.75rem 1.5rem",
            paddingRight: "3rem",
            color: isActive ? "var(--color-primary)" : "var(--color-text-main)",
            // The selected row used to fill with --color-danger-bg. "You are here" is not an error,
            // and while the palette was purple a pale rose passed for a neutral highlight; against
            // navy and gold it just reads as pink. It is the accent's job.
            backgroundColor: isActive ? "var(--color-nav-active)" : "transparent",
            // An inset rail rather than a real border, so nothing shifts by 3px when the selection
            // moves. With a tint this quiet, the rail is what makes the current row unmistakable
            // partway down a long list.
            boxShadow: isActive ? "inset 3px 0 0 var(--color-accent)" : "none",
            fontWeight: isActive ? "600" : "400",
            textDecoration: "none",
            transition: "background-color 0.2s",
          }}
        >
          <Icon
            size={18}
            style={{
              marginRight: "1rem",
              color: isActive
                ? "var(--color-primary)"
                : "var(--color-text-muted)",
            }}
          />
          <span style={{ flex: 1 }}>{t(`nav.modules.${module.id}`, {}, module.name)}</span>
        </Link>
        <button
          onClick={(e) => toggleFavorite(e, module.id)}
          style={{
            position: "absolute",
            right: "1rem",
            top: "50%",
            transform: "translateY(-50%)",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "0.2rem",
          }}
        >
          <Star
            size={16}
            fill={isFav ? "var(--color-warning)" : "none"}
            color={isFav ? "var(--color-warning)" : "var(--color-text-muted)"}
          />
        </button>
      </div>
    );
  };

  const renderCategoryHeader = (catKey, label) =>
    // A heading with its text removed is not a heading, and a chevron on a 72px column has nothing
    // to point at. On the rail the sections become a rule between groups of icons instead.
    rail ? (
      <div
        style={{
          height: "1px",
          backgroundColor: "var(--color-border)",
          margin: "0.5rem 0.75rem",
        }}
      />
    ) : (
    <div
      onClick={() => toggleCategory(catKey)}
      style={{
        padding: "1rem 1.5rem 0.5rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: "pointer",
        color: "var(--color-text-muted)",
      }}
    >
      <span
        style={{
          fontSize: "0.75rem",
          fontWeight: "700",
          textTransform: "uppercase",
        }}
      >
        {t(`nav.sections.${catKey}`, {}, label)}
      </span>
      {collapsed[catKey] ? (
        <ChevronRight size={14} />
      ) : (
        <ChevronDown size={14} />
      )}
    </div>
  );

  const isModuleActive = (moduleId) => {
    // If tenantModules has explicitly set it to false, it's disabled globally for the tenant
    if (tenantModules[moduleId] === false) return false;
    // Otherwise rely on the user-configurable system settings
    if (activeModules[moduleId] === false) return false;

    // Strict Role-Based Access Control from module_access.
    //
    // No default. An absent role used to read as "student", and an unrecognised one used to read as
    // "staff" -- both meant a broken account silently inherited somebody's permissions. Now anything
    // not in the vocabulary is denied outright, and the caller distinguishes "still loading" from
    // "no usable role" so a real user never just sees an empty app with no explanation.
    // normaliseRole never returns anything outside the vocabulary -- an unreadable role comes back
    // as `denied`, so "unknown" and "explicitly denied" collapse into one state that grants nothing.
    const roleStr = normaliseRole(userProfile?.role);
    if (roleStr === ROLE.DENIED) return false;

    // System admins and the platform owner can access everything
    if (isAdminRole(roleStr)) return true;

    const moduleInfo = MODULES.find((m) => m.id === moduleId);

    // Students and guardians open family modules and nothing else. This is the enforcement of that:
    // a staff module has no Student or Parent column in Access Control, so a `studentAccess: "on"`
    // saved before the split would otherwise keep applying while being invisible and uneditable.
    // Where a family needs a staff module's function, they get their own module for it.
    //
    // Must sit AFTER moduleInfo is declared -- reading it above was a temporal dead zone error, and
    // one the build does not catch: `const` is hoisted but unreadable until its declaration runs.
    if (roleStr === ROLE.STUDENT || roleStr === ROLE.PARENT) {
      if (moduleInfo?.audience !== AUDIENCE.FAMILY) return false;
    }

    // module_access stores its columns as teacherAccess / staffAccess / studentAccess / parentAccess,
    // and those keys live in customer data, so the canonical role maps onto the existing key rather
    // than the config being migrated.
    const userBaseRole = ACCESS_KEY_FOR[roleStr];
    if (!userBaseRole) return false;

    // Every module declares its own default now, so there is nothing left to infer. An unknown id
    // -- a module removed from the build while a school still has it saved -- falls to closed.
    const accessConfig = moduleAccessConfig[moduleId] || moduleInfo?.defaultAccess || BY_DUTY;

    const accessLevel = accessConfig[`${userBaseRole}Access`];

    // A config saved before the Parent column existed has no parentAccess key. Fall back to the
    // module's own declared default rather than reading `undefined` as "off" -- otherwise a school
    // that saved any other module's setting would silently strip guardians of the two modules that
    // exist for them. This is a default, not a bypass: Access Control shows it and overrides it.
    if (accessLevel === undefined) {
      const declared = moduleInfo?.defaultAccess?.[`${userBaseRole}Access`];
      if (declared === undefined) return false;
      if (declared === "off") return false;
      if (declared === "on") return true;
    }

    if (accessLevel === "off") return false;
    if (accessLevel === "on") return true;

    if (accessLevel === "some") {
      // Email only. Exception lists hold the address, like every other record in the app -- an
      // initial or a SID is a display label, not an identity, and matching on one would mean this
      // grant could not be expressed as a security rule if it ever needed to be.
      // Run "Migrate exception lists" in Staff Account -> Migration to convert older entries.
      const exceptionList = accessConfig[`${userBaseRole}List`] || [];
      return exceptionList.includes((userProfile?.email || "").toLowerCase());
    }

    return false;
  };

  // The sidebar is the staff shell, so it carries staff modules only. isModuleActive returns true
  // for every module when the caller is an administrator -- that short-circuit sits ahead of the
  // family check, which is why an admin's "Personal Affairs" was listing My Notice beside Notice &
  // Payment and My Messages beside My Message: four family screens no member of staff opens,
  // wearing names one letter apart from the staff modules next to them.
  //
  // Filtered once, here, rather than per section. All four family modules happen to sit in the
  // "family" category today; adding one elsewhere later should not need this to be remembered again.
  // Their routes stay reachable by URL, which is how an office answers "I cannot see my notice".
  const sidebarModules = MODULES.filter(
    (m) => m.audience !== AUDIENCE.FAMILY && isModuleActive(m.id),
  );
  const inSection = (cat) => sidebarModules.filter((m) => m.category === cat);

  const favModules = favorites
    .map((id) => sidebarModules.find((m) => m.id === id))
    .filter(Boolean);


  // A student or guardian gets the tab shell instead of the sidebar. Derived here rather than inside
  // the render branch because the whole layout depends on it, not just one panel.
  const familyRole = normaliseRole(userProfile?.role);
  const isFamily = familyRole === ROLE.STUDENT || familyRole === ROLE.PARENT;
  // Fixed order, so a tab never moves under somebody's thumb between visits: notices first because
  // that is the one with a deadline attached, messages last because it is the only one that is a
  // conversation rather than a record. isModuleActive already filtered these, so what is left is
  // exactly what this account may open.
  const FAMILY_TAB_ORDER = ['my_notice', 'my_attendance', 'my_talent_bank', 'my_messages'];
  const familyTabs = FAMILY_TAB_ORDER
    .map((id) => MODULES.find((m) => m.id === id))
    .filter((m) => m && isModuleActive(m.id));
  // Grouped by what somebody is trying to do, not by which part of the school owns the data. The
  // old split put booking a room under "Personal Affairs" and managing rooms under "Asset
  // Management" -- one subject, two sections, and you had to know which side of it you were on.
  // "Personal Affairs" also held Talent Bank, the widest-scope module in the portal.
  const SIDEBAR_SECTIONS = [
    ["me", "My Work"],
    ["students", "Students"],
    ["assessments", "Assessments"],
    ["administration", "Administration"],
    ["duties", "Duty Arrangement"],
    ["booking", "Booking"],
    ["accounting", "Accounting"],
    ["resources", "Resources"],
    ["system", "System"],
  ];

  const activeSchool = schools.find((s) => s.id === selectedSchoolId);

  const handleForcePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setPasswordChangeError("Passwords do not match!");
      return;
    }
    if (newPassword.length < 6) {
      setPasswordChangeError("Password must be at least 6 characters.");
      return;
    }
    setIsChangingPassword(true);
    setPasswordChangeError("");
    try {
      await updatePassword(auth.currentUser, newPassword);
      // Remove flag in Firestore
      if (selectedSchoolId && user?.email) {
        let accountId = user.email.toLowerCase();
        try {
          const indexDoc = await getDoc(
            doc(db, "schools", selectedSchoolId, "email_index", accountId),
          );
          if (indexDoc.exists() && indexDoc.data().accountId) {
            accountId = indexDoc.data().accountId;
          }
        } catch (e) {}

        const staffRef = doc(
          db,
          "schools",
          selectedSchoolId,
          "staff_profiles",
          accountId,
        );
        const staffDoc = await getDoc(staffRef);
        if (staffDoc.exists()) {
          await setDoc(
            staffRef,
            { requirePasswordChange: false },
            { merge: true },
          );
        } else {
          const studentRef = doc(
            db,
            "schools",
            selectedSchoolId,
            "student_profiles",
            accountId,
          );
          await setDoc(
            studentRef,
            { requirePasswordChange: false },
            { merge: true },
          );
        }
      }
      setRequirePasswordChange(false);
      alert("Password updated successfully!");
    } catch (err) {
      setPasswordChangeError("Failed to update password: " + err.message);
    }
    setIsChangingPassword(false);
  };

  if (requirePasswordChange) {
    return (
      <div
        className="app-shell-vh"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "var(--color-bg-app)",
          padding: "1rem",
        }}
      >
        <div
          style={{
            backgroundColor: "var(--color-bg-card)",
            padding: "2rem",
            borderRadius: "var(--radius-lg)",
            boxShadow: "var(--shadow-md)",
            maxWidth: "400px",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              marginBottom: "1.5rem",
              color: "var(--color-primary)",
            }}
          >
            <Lock size={24} />
            <h2 style={{ fontSize: "1.25rem", fontWeight: "700", margin: 0 }}>
              Update Required
            </h2>
          </div>
          <p
            style={{
              fontSize: "0.875rem",
              color: "var(--color-text-muted)",
              marginBottom: "1.5rem",
            }}
          >
            Your administrator has assigned you a temporary password. For
            security reasons, you must change your password before accessing the
            portal.
          </p>
          <form
            onSubmit={handleForcePasswordChange}
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            {passwordChangeError && (
              <div
                style={{
                  padding: "0.75rem",
                  backgroundColor: 'var(--color-danger-bg)',
                  color: 'var(--color-danger-text)',
                  borderRadius: "var(--radius-md)",
                  fontSize: "0.875rem",
                }}
              >
                {passwordChangeError}
              </div>
            )}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  marginBottom: "0.5rem",
                }}
              >
                New Password
              </label>
              <input
                type="password"
                className="input-field"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  marginBottom: "0.5rem",
                }}
              >
                Confirm New Password
              </label>
              <input
                type="password"
                className="input-field"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            <button
              type="submit"
              className="btn-primary"
              disabled={isChangingPassword}
              style={{ marginTop: "0.5rem", padding: "0.75rem" }}
            >
              {isChangingPassword ? "Updating..." : "Update Password"}
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="btn-outline"
              style={{ padding: "0.75rem", width: "100%" }}
            >
              Cancel & Sign Out
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Signed in, profile resolved, but the role is not one the app knows. Rather than rendering an
  // app with nothing in it -- which reads as an outage -- say what is wrong and who can fix it.
  // Deliberately placed AFTER the loading path so a slow profile fetch never shows this by mistake.
  // Nothing is decided until Firebase has finished looking. Blank for those few milliseconds rather
  // than a spinner or a redirect: both would be a claim about a state nobody knows yet.
  if (!authResolved) return null;

  // Signed out, or signed out while this was open. "/" then decides between the login page and the
  // school picker; it is the one place that logic lives.
  if (!user) return <Navigate to="/" replace />;

  if (user && profileResolved && normaliseRole(userProfile?.role) === ROLE.DENIED) {
    return (
      <div
        className="app-shell-vh"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "1rem",
          padding: "2rem",
          textAlign: "center",
          backgroundColor: "var(--color-bg-app)",
        }}
      >
        <Lock size={38} color="var(--color-text-muted)" />
        <h2 style={{ fontSize: "1.25rem", fontWeight: "700", margin: 0 }}>
          This account has no role assigned
        </h2>
        <p
          style={{
            color: "var(--color-text-muted)",
            fontSize: "0.9rem",
            margin: 0,
            maxWidth: "44ch",
            lineHeight: 1.6,
          }}
        >
          {user.email} is registered with the school but has no role that
          grants access, so there is nothing it can open. Ask a school
          administrator to set the role on your account in Staff or Student
          Management.
        </p>
        <button
          className="btn-outline"
          onClick={() => signOut(auth)}
          style={{ width: "auto", padding: "0 1.25rem", marginTop: "0.5rem" }}
        >
          Sign Out
        </button>
      </div>
    );
  }

  return (
    <div className="app-shell-vh" style={{ display: "flex", overflow: "hidden" }}>
      {/* Sidebar Overlay (Mobile) */}
      {sidebarOpen && (
        <div
          onClick={closeSidebar}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "var(--color-overlay)",
            zIndex: 40,
            opacity: sidebarEntered ? 1 : 0,
            transition: `opacity ${SIDEBAR_TRANSITION_MS}ms ease`,
          }}
        />
      )}

      {/* Sidebar. Staff only -- a family's four screens are the tab bar below, so for them this
          would be 280px of chrome around four links. */}
      {!isFamily && (
      <nav
        className="app-shell-vh"
        style={{
          width: rail ? "72px" : "280px",
          flexShrink: 0,
          backgroundColor: "var(--color-bg-card)",
          borderRight: "1px solid var(--color-border)",
          display: "flex",
          flexDirection: "column",
          position: sidebarOpen ? "fixed" : "relative",
          transform: isMobile ? sidebarTransform : "translateX(0)",
          zIndex: 50,
          transition: sidebarDragging
            ? "none"
            : `transform ${SIDEBAR_TRANSITION_MS}ms ease, width ${SIDEBAR_TRANSITION_MS}ms ease`,
          ...(isMobile && !sidebarOpen
            ? { display: "none" }
            : {}),
        }}
      >
        <div
          onTouchStart={handleSidebarDragStart} onTouchMove={handleSidebarDragMove} onTouchEnd={handleSidebarDragEnd}
          onPointerDown={handleSidebarDragStart} onPointerMove={handleSidebarDragMove} onPointerUp={handleSidebarDragEnd} onPointerCancel={handleSidebarDragEnd}
          style={{
            padding: rail ? "0.9rem 0.5rem" : "1.5rem",
            display: "flex",
            flexDirection: rail ? "column" : "row",
            alignItems: "center",
            justifyContent: "center",
            gap: rail ? "0.5rem" : "0.75rem",
            borderBottom: "1px solid var(--color-border)",
            minHeight: "80px",
            cursor: isMobile ? "grab" : "default",
            touchAction: "pan-y",
          }}
        >
          {/* The school mark is the toggle. A separate chevron would be a second thing in a header
              whose whole point is to get smaller, and the mark is already the one element that
              survives the collapse -- so it is the only place the way back can live.

              A logo that is also a button is not self-evident, so it carries the tooltip and the
              accessible name, and the cursor changes. */}
          {(() => {
            const mark = schoolMetadata?.branding?.logoUrl ? (
              <img
                src={schoolMetadata.branding.logoUrl}
                alt="Logo"
                style={{
                  maxHeight: rail ? "32px" : "48px",
                  maxWidth: rail ? "32px" : "48px",
                  objectFit: "contain",
                  borderRadius: "4px",
                  flexShrink: 0,
                  display: "block",
                }}
              />
            ) : (
              <div
                style={{
                  backgroundColor: "var(--color-primary)",
                  padding: "0.5rem",
                  borderRadius: "8px",
                  color: "white",
                  flexShrink: 0,
                  display: "flex",
                }}
              >
                <School size={rail ? 18 : 24} />
              </div>
            );

            // On a phone the sidebar is a drawer and there is no rail to toggle. The header is also
            // the swipe-to-dismiss handle, and a button sitting in it would swallow the drag.
            if (isMobile) return mark;

            const label = t(rail ? "layout.expandMenu" : "layout.collapseMenu");
            return (
              <button
                type="button"
                onClick={toggleRail}
                title={label}
                aria-label={label}
                aria-expanded={!rail}
                style={{
                  background: "none",
                  border: "none",
                  // Padding rather than a bigger logo: on the rail the mark is the only way back,
                  // so the target has to be comfortable without the school's artwork growing.
                  padding: rail ? "0.35rem" : 0,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                {mark}
              </button>
            );
          })()}

          {!rail && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minWidth: 0,
            }}
          >
            {/* Wraps rather than truncates. This is the school's own name and the one piece of
                the header that has to be right; "Heung Shing Sec..." is not a school. The tagline
                that used to sit under it is gone, so there is room for the second line. */}
            <strong
              style={{
                fontSize: "1.1rem",
                color: "var(--color-primary)",
                lineHeight: 1.25,
                overflowWrap: "anywhere",
              }}
              title={activeSchool?.name || undefined}
            >
              {activeSchool?.name || "School Portal"}
            </strong>

            {/* The tenant control lives here because this is where the school is already named --
                it used to sit in a desktop header bar that repeated the name, alongside a module
                title that repeated the sidebar selection. Both were saying what the sidebar
                already says, so the bar went and the one working part moved here.

                Only rendered when there is actually something to switch between: a developer
                previewing several tenants, or a guardian with children at more than one school. */}
            {/* Only a developer previewing several tenants. A guardian is never offered this:
                one school, one address. */}
            {schools.length > 1 && (
              <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginTop: "0.35rem", minWidth: 0 }}>
                <span
                  style={{
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    color: "var(--color-warning-text)",
                    backgroundColor: "var(--color-warning-bg)",
                    padding: "0.05rem 0.3rem",
                    borderRadius: "3px",
                    flexShrink: 0,
                  }}
                >
                  TESTING
                </span>
                <select
                    value={selectedSchoolId || ""}
                    onChange={(e) => setSelectedSchoolId(e.target.value)}
                    aria-label={t('layout.switchTenant')}
                    style={{
                      border: "1px solid var(--color-border)",
                      background: "var(--color-bg-card)",
                      outline: "none",
                      fontSize: "0.72rem",
                      color: "var(--color-text-subtle)",
                      cursor: "pointer",
                      borderRadius: "4px",
                      padding: "0.1rem 0.2rem",
                      minWidth: 0,
                      maxWidth: "100%",
                    }}
                  >
                    {schools.map((sc) => (
                      <option key={sc.id} value={sc.id}>
                        {sc.name} ({sc.domain})
                      </option>
                    ))}
                  </select>
              </div>
            )}
          </div>
          )}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "1rem 0" }}>
          {/* Commonly Used Modules */}
          {favModules.length > 0 && (
            <div style={{ marginBottom: "1rem" }}>
              {!rail && (
              <div
                style={{
                  padding: "0.5rem 1.5rem",
                  fontSize: "0.75rem",
                  fontWeight: "700",
                  color: "var(--color-primary)",
                  textTransform: "uppercase",
                }}
              >
                {t("nav.commonlyUsed")}
              </div>
              )}
              {favModules.map(renderModuleLink)}
            </div>
          )}

          {/* A heading with nothing under it advertises access the account does not have -- a
              bursar would otherwise see "Students" and find it empty. This is also what keeps the
              list short: a plain teacher's thirteen modules fall into four of these six. */}
          {SIDEBAR_SECTIONS.map(([key, label]) => {
            const mods = inSection(key);
            if (mods.length === 0) return null;
            return (
              <React.Fragment key={key}>
                {renderCategoryHeader(key, label)}
                {/* On the rail there is no heading left to click, so a section collapsed earlier
                    would be modules you cannot reach and cannot see how to reach. The remembered
                    collapse only applies where the chevron is visible. */}
                {(rail || !collapsed[key]) && mods.map(renderModuleLink)}
              </React.Fragment>
            );
          })}
        </div>

        <div
          style={{
            padding: rail ? "0.75rem 0.5rem" : "1.5rem",
            borderTop: "1px solid var(--color-border)",
          }}
        >
          {!rail && (
          <div style={{ marginBottom: "1rem" }}>
            <div
              style={{
                fontSize: "0.875rem",
                fontWeight: "700",
                color: "var(--color-primary)",
                textTransform: "capitalize",
                marginBottom: "0.25rem",
              }}
            >
              {userProfile?.role || "User"}{" "}
              {userProfile?.initial || userProfile?.studentId
                ? `(${userProfile.initial || userProfile.studentId})`
                : ""}
            </div>
            {userProfile?.name && (
              <div
                style={{
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  marginBottom: "0.125rem",
                }}
              >
                {userProfile.name}
              </div>
            )}
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--color-text-muted)",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {user?.email}
            </div>
          </div>
          )}
          {/* Permanent, unlike the banner: somebody who waved the invitation away in week one still
              needs a way to find it in week four. Renders nothing once the app is installed. */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: rail ? "center" : "stretch",
              gap: rail ? "0.35rem" : 0,
            }}
          >
            <InstallMenuItem compact={rail} />
            <PushToggle schoolId={selectedSchoolId} compact={rail} />
          </div>

          {/* In the sidebar rather than buried in settings: somebody who cannot read the interface
              needs to find this without reading the interface, and the sidebar is the one place they
              are already looking. Each language is written IN that language for the same reason --
              "Traditional Chinese" is no help to somebody who only reads Chinese. */}
          <div
            style={{
              display: "flex",
              flexDirection: rail ? "column" : "row",
              gap: "0.35rem",
              margin: rail ? "0.5rem 0" : "0 0 0.5rem",
            }}
          >
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                onClick={() => setLanguage(l.code)}
                aria-pressed={language === l.code}
                title={rail ? l.label : undefined}
                aria-label={rail ? l.label : undefined}
                style={{
                  flex: 1,
                  padding: "0.35rem",
                  fontSize: "0.78rem",
                  borderRadius: "var(--radius-md)",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  fontWeight: language === l.code ? "700" : "500",
                  backgroundColor: language === l.code ? "var(--color-nav-active)" : "transparent",
                  border: `1px solid ${language === l.code ? "var(--color-accent)" : "var(--color-border)"}`,
                  color: language === l.code ? "var(--color-text-main)" : "var(--color-text-subtle)",
                }}
              >
                {rail ? l.short : l.label}
              </button>
            ))}
          </div>

          <button
            onClick={handleLogout}
            className="btn-outline"
            title={rail ? t("common.signOut") : undefined}
            aria-label={rail ? t("common.signOut") : undefined}
            style={{
              padding: "0.5rem",
              fontSize: "0.875rem",
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: rail ? 0 : "0.4rem",
            }}
          >
            <LogOut size={16} /> {!rail && t("common.signOut")}
          </button>
        </div>
      </nav>
      )}

      {/* Main Content Area */}
      <div
        className="app-shell-vh"
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Families get their own top bar at every size: with no sidebar, this is the only place
            the school name, the signed-in identity and Sign out can live. */}
        {isFamily && (
          <FamilyTopBar
            schoolName={activeSchool?.name}
            logoUrl={schoolMetadata?.branding?.logoUrl}
            userProfile={userProfile}
            email={user?.email}
            onSignOut={handleLogout}
            isMobile={isMobile}
            headerActionsRef={setHeaderActionsEl}
            schoolId={selectedSchoolId}
          />
        )}

        {/* On a desktop the tabs sit under the school name; on a phone they sit at the bottom,
            after <main>. Same component either way. */}
        {isFamily && !isMobile && (
          <FamilyTabBar modules={familyTabs} pathname={location.pathname} isMobile={false} />
        )}

        {/* Top Header */}
        {/* Staff, mobile only. On desktop this bar held a module title and the school name, and the
            sidebar already carries both -- the title repeated the sidebar selection and the badge
            repeated the sidebar heading, so the whole row was ~64px of chrome saying nothing new.
            The one part that did work, the tenant switcher, moved up into the sidebar heading.

            On mobile the bar stays because it is load-bearing: it holds the only way to open the
            sidebar, and it is where HeaderActionsPortal parks each module's controls. */}
        {isMobile && !isFamily && (
          <header
            style={{
              backgroundColor: "var(--color-bg-card)",
              borderBottom: "1px solid var(--color-border)",
              padding: "0.5rem 0.75rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <button
              onClick={openSidebar}
              className="btn-outline"
              style={{ padding: "0.5rem", border: "none" }}
              aria-label={t("layout.openMenu")}
            >
              <Menu size={24} />
            </button>

            <div
              ref={setHeaderActionsEl}
              style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap", justifyContent: "flex-end", minWidth: 0 }}
            />
          </header>
        )}

        {/* Dynamic Page Content -- no special bottom padding needed for the tabs bar anymore: it
            reflows to the bottom of each module's own flex column on mobile (see theme-palette.css)
            instead of overlaying via position:fixed, so there's no separate space to reserve here. */}
        <main style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: isMobile ? "0" : "1.5rem" }}>
          {/* Above the module, not inside it: every screen gets it without a single module knowing
              this exists. Shows itself only when there is something a browser can actually do. */}
          <InstallBanner isMobile={isMobile} />
          {selectedSchoolId ? (
            (() => {
              const currentPath = location.pathname;

              // Families have no dashboard. Sign-in lands on /dashboard, which matches the dashboard
              // module -- a staff module -- so the guard below would show them "not available" on
              // their own first screen. Redirect before the guard runs, not inside DashboardHome:
              // that component never renders for them, because the guard intercepts first.
              if (isFamily && currentPath.replace(/\/$/, "") === "/dashboard") {
                return <Navigate to="/dashboard/my-notice" replace />;
              }

              const matchedModule = [...MODULES]
                .sort((a, b) => b.url.length - a.url.length)
                .find((m) => currentPath.startsWith(m.url));

              // Nothing can be said about a module until the configuration and the role are both
              // known. Until then this is a wait, not a verdict -- and on a hard refresh it is the
              // difference between a moment of "loading" and being told the module is switched off.
              if (matchedModule && (!accessReady || !userProfile)) {
                return (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      color: "var(--color-text-muted)",
                      gap: "0.6rem",
                    }}
                  >
                    <Loader2 size={18} className="animate-spin" />
                    <span style={{ fontSize: "0.9rem" }}>{t("common.loading")}</span>
                  </div>
                );
              }

              const isActive =
                !matchedModule || isModuleActive(matchedModule.id);

              if (!isActive) {
                return (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      color: "var(--color-text-muted)",
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: "var(--color-bg-app)",
                        padding: "1rem",
                        borderRadius: "50%",
                        marginBottom: "1rem",
                      }}
                    >
                      <LogOut size={48} style={{ opacity: 0.5 }} />
                    </div>
                    <h2
                      style={{
                        fontSize: "1.5rem",
                        fontWeight: "600",
                        marginBottom: "0.5rem",
                        color: "var(--color-text-main)",
                      }}
                    >
                      {t("layout.moduleOff")}
                    </h2>
                    <p>{t("layout.moduleOffDetail")}</p>
                  </div>
                );
              }
              return (
                <Outlet
                  context={{
                    activeSchoolId: selectedSchoolId,
                    activeSchool,
                    schoolMetadata,
                    userProfile,
                    user,
                    headerActionsEl,
                    isMobile,
                    // Lets a module retract the mobile sidebar itself -- e.g. when opening a modal,
                    // which would otherwise sit underneath the open sidebar overlay.
                    closeSidebar,
                  }}
                />
              );
            })()
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "var(--color-text-muted)",
              }}
            >
              <School
                size={48}
                style={{ marginBottom: "1rem", opacity: 0.5 }}
              />

                  <h2
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "600",
                      marginBottom: "0.5rem",
                      color: "var(--color-text-main)",
                    }}
                  >
                    No School Assigned
                  </h2>
                  <p>
                    Your account is not currently assigned to any school tenant.
                  </p>
                  <p>
                    If you are a Developer, please add this email as a Staff member
                    to a school tenant to access the portal.
                  </p>
                
            </div>
          )}
        </main>

        {/* Last child of the column on a phone, so it pushes <main> up instead of overlaying it.
            position:fixed here would need <main> to reserve matching bottom padding, and that dead
            strip is exactly the bug the module tabs bar had before it was reflowed the same way. */}
        {isFamily && isMobile && (
          <FamilyTabBar modules={familyTabs} pathname={location.pathname} isMobile />
        )}
      </div>
    </div>
  );
};

export default Layout;
